import { UnsplashCredit } from '../common/UnsplashCredit';
import React, { useRef, useState } from 'react';
import { Camera, ChevronRight } from 'lucide-react';
import { Badge } from '../common/Badge';
import { typeOptions, type EditingItem, type GroupedItems } from '../../hooks/useItineraryPage';
import type { DayImage, ItineraryItem } from '../../types';
import { useActivityImage } from '../../hooks/useActivityImage';
import { useReportedPhotoTier } from '../../hooks/useUnsplashCredit';
import { thumbCredit, creditHref } from '../../utils/unsplashCredit';
import { ConfirmDialog } from '../common/Modal';
import { useOptionalTheme } from '../../contexts/ThemeContext';
import { sunsetTagFor, type TagPlan } from '../../utils/sunsetTag';
import SunsetTag from './SunsetTag';

/**
 * Who can see a photograph once it is on a plan, said before it is sent.
 * Exact, and deliberately not "your link is already out": whether anyone was
 * sent the link is not something the app knows, and a disclosure must not claim
 * knowledge it lacks. Only once it is answered yes does the upload carry
 * `audience_acknowledged`, the one field the server records consent from.
 */
export const PHOTO_AUDIENCE = "Anyone with this plan's link can see this photo.";

// Group items into Morning / Afternoon / Evening blocks by time
function getTimeBlock(time: string | null): string {
  if (!time) return 'Morning';
  const hour = parseInt(time.split(':')[0], 10);
  if (hour < 12) return 'Morning';
  if (hour < 17) return 'Afternoon';
  return 'Evening';
}

interface TimeBlock {
  label: string;
  items: ItineraryItem[];
}

function groupIntoBlocks(items: ItineraryItem[]): TimeBlock[] {
  const blockMap = new Map<string, ItineraryItem[]>();
  for (const item of items) {
    const label = getTimeBlock(item.time);
    if (!blockMap.has(label)) blockMap.set(label, []);
    blockMap.get(label)!.push(item);
  }
  const order = ['Morning', 'Afternoon', 'Evening'];
  return order
    .filter(l => blockMap.has(l))
    .map(l => ({ label: l, items: blockMap.get(l)! }));
}

function daySummary(items: ItineraryItem[]): string {
  const names = items.filter(i => i.name).slice(0, 3).map(i => i.name);
  if (names.length === 0) return '';
  return names.join(', ') + (items.length > 3 ? '...' : '');
}

/**
 * One activity's picture, whichever rung of the ladder can supply it.
 *
 * Its own component because the resolution is a hook, and a hook cannot live
 * inside the row's render loop. Renders nothing at all when no rung has an
 * image — an activity with no picture is a normal state, and a placeholder box
 * would be noise on every "Lunch" and "Check in" in the plan.
 */
const ActivityThumb: React.FC<{
  item: ItineraryItem;
  onRemove?: (itemId: number) => void;
  onTier?: (itemId: number, tier: string | undefined) => void;
  /** Present only where the owner can act on their own plan. */
  onAcknowledgeShare?: (itemId: number, photoUrl: string) => void;
}> = ({ item, onRemove, onTier, onAcknowledgeShare }) => {
  const image = useActivityImage(item.image, 200);
  // Which rung won is only knowable here, and it can change after mount when a
  // venue photo 404s and demotes. The credit footer needs the rung that is
  // actually showing, not the rungs the payload happened to carry.
  const shownTier = image.url ? image.tier : undefined;
  useReportedPhotoTier(item.id, shownTier, onTier);
  if (!image.url) return null;

  // Their own photo keeps its remove control; the other rungs are not theirs
  // to delete.
  if (image.tier === 'user') {
    // Their own photograph that is not on their shared link yet, because it
    // predates the release that started drawing these there. Said beside the
    // photograph, with the one action that changes it — a fact, not a warning.
    // The sentence is conditional because until they add it, nobody with the
    // link can see it.
    const withheld = Boolean(item.image?.withheld_from_share && onAcknowledgeShare);
    return (
      <>
        <div className="day-card-photo-thumb-wrap">
          <img
            src={image.url}
            alt={item.name}
            className="day-card-photo-thumb"
            onError={image.onError}
          />
          {onRemove && (
            <button
              className="day-card-photo-remove"
              onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
              title="Remove photo" aria-label="Remove photo"
            >&times;</button>
          )}
        </div>
        {withheld && (
          <div className="day-card-photo-withheld">
            {/* "This plan's link", not "your shared link": the flag is sent
                whether or not the plan has been shared yet. */}
            <span>
              Not on this plan&rsquo;s link. If you add it, anyone with this
              plan&rsquo;s link can see this photo.
            </span>
            <button
              type="button"
              className="day-card-photo-withheld-add"
              aria-label={`Add the photo for ${item.name} to this plan's link`}
              // The photograph the note sits beside — so the answer cannot
              // land on a replacement made elsewhere since this page loaded.
              onClick={(e) => { e.stopPropagation(); onAcknowledgeShare!(item.id, item.image!.user_photo_url!); }}
            >
              Add it to the link
            </button>
          </div>
        )}
      </>
    );
  }

  // The item's own name, per the spec's accessible-name contract for this tier.
  // Not `alt=""`: these thumbnails carry information the row's text does not —
  // whose photo it is and which rung supplied it — and a reader that skips them
  // silently cannot tell a plan with pictures from one without.
  const thumb = (
    <img
      className="day-card-photo-thumb"
      src={image.url}
      alt={item.name}
      loading="lazy"
      onError={image.onError}
    />
  );

  // Credit whichever tier actually rendered — Google requires the photographer
  // and a route to the place on Maps; Unsplash requires the photographer and a
  // link back. A thumbnail this size may carry the credit as a tap-through
  // rather than as visible text, which is what this link is: it opens the full
  // attribution at the source, and its title states the credit for anyone
  // hovering or using a screen reader.
  const credit = image.photographer
    ? thumbCredit(image.photographer, image.tier)
    : 'View this photo at its source';
  // No link to credit through to (a venue photo Google returned without a
  // Maps URI): the credit still has to be readable, so carry it on a labelled
  // wrapper rather than dropping it. Mirrors what mobile does for this case.
  if (!image.creditUrl) {
    return image.photographer ? (
      <span title={credit}>{thumb}<span className="sr-only">{credit}</span></span>
    ) : thumb;
  }
  return (
    <a
      className="day-card-photo-credit-link"
      href={creditHref(image.creditUrl, image.tier)}
      target="_blank"
      rel="noopener noreferrer nofollow"
      title={credit}
      onClick={(e) => e.stopPropagation()}
    >
      {thumb}
      <span className="sr-only">{credit}</span>
    </a>
  );
};

interface TimelineViewProps {
  groupedItems: GroupedItems;
  /** Per-date Unsplash photo, keyed as the API returns it (YYYY-MM-DD). */
  dayImages?: Record<string, DayImage | null>;
  selectedDay?: string | null;
  isEditMode: boolean;
  setIsEditMode: (mode: boolean) => void;
  onEnterEditMode: () => void;
  onActivityTap?: (item: ItineraryItem, dayNum: number) => void;
  onViewDayMap?: (date: string) => void;
  editingItem: EditingItem | null;
  draggedItem: number | null;
  expandedNotes: Set<number>;
  collapsedDays: Set<string>;
  onToggleDayCollapsed: (date: string) => void;
  /** Reports which rung of the ladder each activity is actually showing, so the
   *  page can credit Unsplash for the photos on screen rather than for the ones
   *  the payload happened to carry. */
  onActivityPhotoTier?: (itemId: number, tier: string | undefined) => void;
  onCollapseAll: () => void;
  onExpandAll: () => void;
  formatShortDate: (dateStr: string) => string;
  formatTime: (timeStr: string | null) => string;
  getEventStatus: (item: ItineraryItem) => string;
  onStartEdit: (itemId: number, field: EditingItem['field'], currentValue: string) => void;
  onEditChange: (value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onAddItem: (date: string) => void;
  onDeleteItem: (itemId: number) => void;
  onDragStart: (itemId: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (targetItemId: number, date: string) => void;
  onToggleNoteExpanded: (itemId: number) => void;
  onCheckin?: (itemId: number, status: 'completed' | 'skipped' | 'pending') => void;
  onFavorite?: (itemId: number) => void;
  lifecycleState?: string;
  itineraryId?: number;
  /** `audienceAcknowledged` is true only after "Use this photo" was pressed. */
  onPhotoUpload?: (itemId: number, file: File, audienceAcknowledged: boolean) => Promise<string>;
  onPhotoDelete?: (itemId: number) => Promise<void>;
  /** Lets a photograph that predates the share-page release onto the shared link. */
  onPhotoAcknowledgeShare?: (itemId: number, photoUrl: string) => Promise<void>;
  mode?: 'solo' | 'visiting' | 'group';
  isLocked?: boolean;
  onVote?: (itemId: number, sentiment: 'interested' | 'not_for_me') => void;
  /**
   * The plan's clock and destination for the SUNSET tag (Almanac S3): its
   * serialized `effective_timezone`, and destination coordinates when the payload
   * carries them. Absent, no row is tagged.
   */
  sunsetClock?: TagPlan;
}

export const TimelineView: React.FC<TimelineViewProps> = ({
  groupedItems,
  dayImages,
  selectedDay,
  isEditMode,
  setIsEditMode,
  onEnterEditMode,
  onActivityTap,
  onViewDayMap,
  editingItem,
  draggedItem,
  expandedNotes,
  collapsedDays,
  onToggleDayCollapsed,
  onActivityPhotoTier,
  onCollapseAll,
  onExpandAll,
  formatShortDate,
  formatTime,
  getEventStatus,
  onStartEdit,
  onEditChange,
  onSaveEdit,
  onCancelEdit,
  onKeyDown,
  onAddItem,
  onDeleteItem,
  onDragStart,
  onDragOver,
  onDrop,
  onToggleNoteExpanded,
  onCheckin,
  onFavorite,
  lifecycleState,
  itineraryId,
  onPhotoUpload,
  onPhotoDelete,
  onPhotoAcknowledgeShare,
  mode,
  isLocked,
  onVote,
  sunsetClock,
}) => {
  const isCompleted = lifecycleState === 'completed';
  // The tag preference lives in Settings' Timetable row (default on).
  const tagSunsetStops = useOptionalTheme()?.tagSunsetStops ?? true;
  const isSunsetRow = (item: ItineraryItem) =>
    tagSunsetStops && !!sunsetClock && sunsetTagFor(item, sunsetClock);
  const [uploadingItemId, setUploadingItemId] = useState<number | null>(null);
  const [localPhotoUrls, setLocalPhotoUrls] = useState<Record<number, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingItemId, setPendingItemId] = useState<number | null>(null);

  const handlePhotoClick = (itemId: number) => {
    setPendingItemId(itemId);
    fileInputRef.current?.click();
  };

  // The chosen file waits here until the user has been told who can see it.
  // The browser's file dialog cannot carry a sentence of ours, so the audience
  // is said after the choice and before anything is sent.
  const [awaitingAudience, setAwaitingAudience] = useState<{ itemId: number; file: File } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared now rather than after the upload, so choosing the same file again
    // after declining still fires a change event.
    e.target.value = '';
    if (!file || !pendingItemId || !onPhotoUpload) return;
    const itemId = pendingItemId;
    setPendingItemId(null);
    setAwaitingAudience({ itemId, file });
  };

  const confirmAudience = async () => {
    if (!awaitingAudience || !onPhotoUpload) return;
    const { itemId, file } = awaitingAudience;
    setAwaitingAudience(null);
    setUploadingItemId(itemId);
    try {
      // `true` because the question was answered yes, and only then.
      const url = await onPhotoUpload(itemId, file, true);
      setLocalPhotoUrls(prev => ({ ...prev, [itemId]: url }));
    } catch { /* silent */ }
    setUploadingItemId(null);
  };

  const handleAcknowledgeShare = async (itemId: number, photoUrl: string) => {
    if (!onPhotoAcknowledgeShare) return;
    try {
      await onPhotoAcknowledgeShare(itemId, photoUrl);
    } catch { /* silent — the note stays, so the action is still there */ }
  };

  const handlePhotoDelete = async (itemId: number) => {
    if (!onPhotoDelete) return;
    try {
      await onPhotoDelete(itemId);
      setLocalPhotoUrls(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    } catch { /* silent */ }
  };

  const allEntries = Object.entries(groupedItems);
  const dayEntries = selectedDay
    ? allEntries.filter(([date]) => date === selectedDay)
    : allEntries;

  // Track activity index per day for numbered markers
  const activityCounters = new Map<string, number>();

  const renderActivity = (item: ItineraryItem, isLast: boolean, date: string, dayNum: number) => {
    const count = (activityCounters.get(date) || 0) + 1;
    activityCounters.set(date, count);
    const eventStatus = getEventStatus(item);
    const checkinLabel = item.activity_status === 'completed' ? 'Completed' : item.activity_status === 'skipped' ? 'Skipped' : 'Tap to check in';
    return (
      <div
        key={item.id}
        className={`day-card-activity ${isEditMode ? 'editable' : ''} ${draggedItem === item.id ? 'dragging' : ''} ${eventStatus}-event`}
        draggable={isEditMode}
        onDragStart={() => onDragStart(item.id)}
        onDragOver={onDragOver}
        onDrop={() => onDrop(item.id, date)}
        onClick={(e) => { if (!isEditMode) { e.stopPropagation(); onActivityTap?.(item, dayNum); } }}
        style={{ cursor: isEditMode ? 'default' : 'pointer' }}
      >
        {isEditMode && <div className="drag-handle" title="Drag to reorder">⋮⋮</div>}
        <div className="day-card-dot-col">
          {onCheckin && !isEditMode ? (
            <button
              className={`checkin-circle checkin-${item.activity_status || 'pending'}`}
              onClick={(e) => {
                e.stopPropagation();
                const current = item.activity_status || 'pending';
                const next = current === 'pending' ? 'completed' : current === 'completed' ? 'skipped' : 'pending';
                onCheckin(item.id, next);
              }}
              title={checkinLabel} aria-label={checkinLabel}
            >
              {item.activity_status === 'completed' && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
              {item.activity_status === 'skipped' && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              )}
              {(!item.activity_status || item.activity_status === 'pending') && <span>{count}</span>}
            </button>
          ) : (
            <div className="day-card-num">{count}</div>
          )}
          {!isLast && <div className="day-card-connector" />}
        </div>
        <div className="day-card-activity-content">
          {/* Activity photo */}
          {!isEditMode && (() => {
            const uploaded = localPhotoUrls[item.id] ?? item.photo_url;
            // A just-uploaded photo is shown directly (it is not in the payload
            // yet); anything else goes through the ladder, so a broken user
            // photo falls through to the venue or pool rung instead of leaving
            // a broken frame on the row.
            const photoUrl = localPhotoUrls[item.id];
            return (
              <div className="day-card-photo-row">
                {photoUrl ? (
                  <div className="day-card-photo-thumb-wrap">
                    {/* Shown directly because it is not in the payload yet.
                        It still needs a way to fail: dropping the local URL
                        hands the row back to the ladder rather than leaving a
                        broken frame. */}
                    <img
                      src={photoUrl}
                      alt={item.name}
                      className="day-card-photo-thumb"
                      onError={() => setLocalPhotoUrls((prev) => {
                        const next = { ...prev };
                        delete next[item.id];
                        return next;
                      })}
                    />
                    {onPhotoDelete && (
                      <button
                        className="day-card-photo-remove"
                        onClick={(e) => { e.stopPropagation(); handlePhotoDelete(item.id); }}
                        title="Remove photo" aria-label="Remove photo"
                      >&times;</button>
                    )}
                  </div>
                ) : (
                  <>
                    {/* The ladder: their own photo, else the venue, else the
                        pool photo. Renders nothing when no rung has one, so the
                        "add a photo" affordance stays reachable. */}
                    <ActivityThumb
                      item={item}
                      onRemove={onPhotoDelete ? handlePhotoDelete : undefined}
                      onTier={onActivityPhotoTier}
                      onAcknowledgeShare={onPhotoAcknowledgeShare ? handleAcknowledgeShare : undefined}
                    />
                    {onPhotoUpload && !uploaded && (
                      uploadingItemId === item.id ? (
                        <div className="day-card-photo-uploading">Uploading...</div>
                      ) : (
                        <button
                          className="day-card-photo-add"
                          onClick={(e) => { e.stopPropagation(); handlePhotoClick(item.id); }}
                          title="Add photo"
                        >
                          <Camera size={14} /> Photo
                        </button>
                      )
                    )}
                  </>
                )}
              </div>
            );
          })()}
          {/* Time */}
          {item.time && !isEditMode && (
            <div className="day-card-activity-time">{formatTime(item.time)}</div>
          )}
          {/* Title + Favorite */}
          <div className="day-card-title-row">
            <div
              className={`day-card-activity-name ${isEditMode ? 'clickable' : ''}`}
              onClick={() => onStartEdit(item.id, 'name', item.name)}
            >
              {editingItem?.id === item.id && editingItem?.field === 'name' ? (
                <input type="text" value={editingItem.value} onChange={(e) => onEditChange(e.target.value)} onBlur={onSaveEdit} onKeyDown={onKeyDown} autoFocus className="inline-input" />
              ) : item.name}
              {isSunsetRow(item) && <SunsetTag />}
            </div>
            {item.confirmed && (
              <Badge
                variant="fill"
                className="day-card-confirmed"
                aria-label="Confirmed — AI chat won't change this"
              >
                CONFIRMED
              </Badge>
            )}
            {onFavorite && !isEditMode && (
              <button
                className={`day-card-favorite ${item.is_favorite ? 'day-card-favorite--active' : ''}`}
                onClick={(e) => { e.stopPropagation(); onFavorite(item.id); }}
                aria-label={item.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill={item.is_favorite ? 'var(--accent-primary)' : 'none'} stroke={item.is_favorite ? 'var(--accent-primary)' : 'var(--border)'} strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
              </button>
            )}
          </div>
          {/* Place */}
          {(item.location || isEditMode) && (
            <div
              className={`day-card-activity-place ${isEditMode ? 'clickable' : ''}`}
              onClick={() => onStartEdit(item.id, 'location', item.location || '')}
            >
              {editingItem?.id === item.id && editingItem?.field === 'location' ? (
                <input type="text" value={editingItem.value} onChange={(e) => onEditChange(e.target.value)} onBlur={onSaveEdit} onKeyDown={onKeyDown} autoFocus placeholder="Add location" className="inline-input" />
              ) : item.location || (isEditMode ? 'Add location' : '')}
            </div>
          )}
          {/* Description (notes) */}
          {item.notes && !isEditMode && (
            <div className="day-card-activity-desc">
              {item.notes.length > 120 && !expandedNotes.has(item.id)
                ? <>{item.notes.substring(0, 120)}... <span className="read-more-btn" onClick={() => onToggleNoteExpanded(item.id)}>more</span></>
                : <>{item.notes}{item.notes.length > 120 && <> <span className="read-more-btn" onClick={() => onToggleNoteExpanded(item.id)}>less</span></>}</>
              }
            </div>
          )}
          {isEditMode && (
            <div className="item-notes-container">
              {editingItem?.id === item.id && editingItem?.field === 'notes' ? (
                <textarea value={editingItem.value} onChange={(e) => onEditChange(e.target.value)} onBlur={onSaveEdit} onKeyDown={(e) => { if (e.key === 'Escape') onCancelEdit(); }} autoFocus placeholder="Add notes" className="inline-input inline-textarea" />
              ) : !item.notes ? (
                <button className="add-notes-btn" onClick={() => onStartEdit(item.id, 'notes', '')}>+ Notes</button>
              ) : (
                <div className="day-card-activity-desc clickable" onClick={() => onStartEdit(item.id, 'notes', item.notes)}>{item.notes}</div>
              )}
            </div>
          )}
          {/* Validation warnings */}
          {!isEditMode && item.closed_days && item.closed_days.length > 0 && (() => {
            const itemDay = new Date(item.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
            const isClosed = item.closed_days!.some(d => d.toLowerCase() === itemDay.toLowerCase());
            return isClosed ? (
              <div className="activity-warning">
                <span className="activity-warning-icon">&#9888;</span>
                Closed {item.closed_days!.join(', ')}
              </div>
            ) : null;
          })()}
          {!isEditMode && item.reservation_note && (
            <div className="activity-info">
              <span className="activity-info-icon">&#8505;</span>
              {item.reservation_note}
              {item.booking_url && (
                <a href={item.booking_url} target="_blank" rel="noopener noreferrer" className="activity-book-link">
                  Book &#8599;
                </a>
              )}
            </div>
          )}
          {!isEditMode && !item.reservation_note && item.booking_url && (
            <div className="activity-info">
              <a href={item.booking_url} target="_blank" rel="noopener noreferrer" className="activity-book-link">
                Book &#8599;
              </a>
            </div>
          )}
          {/* Duration */}
          {(item.duration || isEditMode) && (
            <div
              className={`day-card-activity-dur ${isEditMode ? 'clickable' : ''}`}
              onClick={() => isEditMode && onStartEdit(item.id, 'duration', item.duration || '')}
            >
              {editingItem?.id === item.id && editingItem?.field === 'duration' ? (
                <input type="text" value={editingItem.value} onChange={(e) => onEditChange(e.target.value)} onBlur={onSaveEdit} onKeyDown={onKeyDown} autoFocus placeholder="e.g. 2 hours" className="inline-input" />
              ) : item.duration || (isEditMode ? 'Add duration' : '')}
            </div>
          )}
          {/* Edit-mode extras */}
          {isEditMode && (
            <>
              {item.type && item.type !== 'activity' && (
                <div className="item-type clickable" onClick={() => onStartEdit(item.id, 'type', item.type || 'activity')}>
                  {editingItem?.id === item.id && editingItem?.field === 'type' ? (
                    <select value={editingItem.value} onChange={(e) => { onEditChange(e.target.value); setTimeout(onSaveEdit, 100); }} onBlur={onSaveEdit} autoFocus className="inline-select">
                      {typeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  ) : <span className="item-type-label">{item.type}</span>}
                </div>
              )}
              <div className="timeline-time clickable" onClick={() => onStartEdit(item.id, 'time', item.time || '')}>
                {editingItem?.id === item.id && editingItem?.field === 'time' ? (
                  <input type="time" value={editingItem.value} onChange={(e) => onEditChange(e.target.value)} onBlur={onSaveEdit} onKeyDown={onKeyDown} autoFocus className="inline-input time-input" />
                ) : formatTime(item.time) || 'Set time'}
              </div>
              <button className="delete-item-btn" onClick={() => onDeleteItem(item.id)} title="Delete activity" aria-label="Delete activity">🗑️</button>
            </>
          )}
          {/* Group vote buttons */}
          {mode === 'group' && !isLocked && !isEditMode && onVote && (
            <div className="day-card-vote-row">
              <button
                className="day-card-vote-btn day-card-vote-btn--up"
                onClick={(e) => { e.stopPropagation(); onVote(item.id, 'interested'); }}
                title="Interested" aria-label="Interested"
              >
                👍
              </button>
              <button
                className="day-card-vote-btn day-card-vote-btn--down"
                onClick={(e) => { e.stopPropagation(); onVote(item.id, 'not_for_me'); }}
                title="Not for me" aria-label="Not for me"
              >
                👎
              </button>
              {item.vote_summary && (
                <span className="day-card-vote-counts">
                  {item.vote_summary.interested > 0 && <span className="vote-count-interested">{item.vote_summary.interested} interested</span>}
                  {item.vote_summary.interested > 0 && item.vote_summary.not_for_me > 0 && ' · '}
                  {item.vote_summary.not_for_me > 0 && <span className="vote-count-not-for-me">{item.vote_summary.not_for_me} not for me</span>}
                </span>
              )}
            </div>
          )}
          {/* Vote counts (read-only when locked) */}
          {mode === 'group' && isLocked && !isEditMode && item.vote_summary && (
            <div className="day-card-vote-row day-card-vote-row--readonly">
              <span className="day-card-vote-counts">
                {item.vote_summary.interested > 0 && <span className="vote-count-interested">👍 {item.vote_summary.interested}</span>}
                {item.vote_summary.interested > 0 && item.vote_summary.not_for_me > 0 && ' · '}
                {item.vote_summary.not_for_me > 0 && <span className="vote-count-not-for-me">👎 {item.vote_summary.not_for_me}</span>}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="day-by-day-header">
        <span className="day-by-day-label">Day by Day</span>
        {allEntries.length >= 2 && (
          <button
            className="day-by-day-toggle"
            onClick={collapsedDays.size >= allEntries.length ? onExpandAll : onCollapseAll}
          >
            {collapsedDays.size >= allEntries.length ? 'Expand all' : 'Collapse all'}
          </button>
        )}
      </div>

      <ConfirmDialog
        isOpen={awaitingAudience !== null}
        onClose={() => setAwaitingAudience(null)}
        onConfirm={() => { void confirmAudience(); }}
        title="Use this photo?"
        message={PHOTO_AUDIENCE}
        confirmText="Use this photo"
      />

      {/* Hidden file input for photo uploads */}
      {isCompleted && onPhotoUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      )}

      {dayEntries.map(([date, items]) => {
        const isCollapsed = collapsedDays.has(date);
        const globalDayIndex = allEntries.findIndex(([d]) => d === date);
        const dayNum = globalDayIndex + 1;
        const summary = daySummary(items);
        const blocks = groupIntoBlocks(items);

        return (
          <div key={date} className={`day-card${isCollapsed ? ' day-card--collapsed' : ''}`}>
            <div
              className="day-card-header"
              role="button"
              tabIndex={0}
              aria-expanded={!isCollapsed}
              onClick={() => onToggleDayCollapsed(date)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleDayCollapsed(date); }
              }}
            >
              {dayImages?.[date] && (
                /* Decorative: the day is already named by the label beside it,
                   so an alt would only repeat it to a screen reader. */
                <img
                  className="day-card-day-thumb"
                  src={dayImages[date]!.thumb_url || dayImages[date]!.url}
                  alt=""
                  loading="lazy"
                />
              )}
              <div className="day-card-header-body">
                <span className="day-card-day-label">Day {dayNum} &middot; {formatShortDate(date)}</span>
                <span className="day-card-title">{items.length} activities{summary ? ` · ${summary}` : ''}</span>
              </div>
              <ChevronRight size={16} className={`day-card-chevron ${!isCollapsed ? 'day-card-chevron--open' : ''}`} />
            </div>
            {/* The day thumbnail's OWN photographer (r775 finding 2). The
                page footer said "Photos by Unsplash", which names the library
                and not the person — the terms ask for the photographer and a
                link to their profile, per image. Outside the header, because
                the header is the day's toggle and a link inside it would be
                unreachable. */}
            {dayImages?.[date]?.photographer && dayImages[date]!.unsplash_link && (
              <UnsplashCredit
                photographer={dayImages[date]!.photographer!}
                profileLink={dayImages[date]!.unsplash_link!}
                className="day-card-photo-credit"
              />
            )}

            {!isCollapsed && (
              <div className="day-card-body">
                {blocks.map((block) => (
                  <div key={block.label} className="day-card-block">
                    <div className="day-card-block-label">{block.label}</div>
                    {block.items.map((item, ai) =>
                      renderActivity(item, ai === block.items.length - 1, date, dayNum)
                    )}
                  </div>
                ))}
                <button className="day-card-add-activity" onClick={(e) => { e.stopPropagation(); onAddItem(date); }}>
                  + Add activity to Day {dayNum}
                </button>
                {onViewDayMap && (
                  <button className="day-card-view-map" onClick={(e) => { e.stopPropagation(); onViewDayMap(date); }}>
                    View full day with map <ChevronRight size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default TimelineView;
